import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Loader } from '@elfsight/widget-ui';
import { useWidgetRect } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { meta } from '../../meta';
import { getSecondColor } from '../helpers';
import { CategoryList } from './categories/CategoryList';
import { EmptyCategory } from './categories/EmptyCategory';
import { useImages } from './images';
import { LoadMore } from './items/LoadMore';
import { Layout } from './layouts/Layout';
import { useImageLazyLoading } from './lazy-loading';
import { Lightbox, useLightbox } from './lightbox';

const MOBILE_MAX_WIDTH = 420;
const TABLET_MAX_WIDTH = 768;

function LoadMoreButtonContainer({ text, handleClick, disabled }) {
  const { allImagesLoaded } = useImageLazyLoading();
  const {
    backgroundType,
    backgroundColor,
    backgroundImageOverlayColor,
    loadMoreButtonColor
  } = useSettings();

  const secondColor = getSecondColor({
    backgroundType,
    backgroundColor,
    backgroundImageOverlayColor
  });

  const content = allImagesLoaded ? (
    text
  ) : (
    <Loader size={20} color={secondColor} />
  );

  return (
    <LoadMoreButton
      onClick={handleClick}
      disabled={disabled}
      $loadMoreButtonColor={loadMoreButtonColor}
      role="button"
    >
      {content}
    </LoadMoreButton>
  );
}

export const GalleryContainer = () => {
  const { filtersEnabled, categories } = useSettings();
  const { images } = useImages();
  const { sources } = useLightbox();

  const [selectedCategory, setSelectedCategory] = useState('*');
  const windowWidth = useWidgetRect()?.width;
  const { allImagesLoaded } = useImageLazyLoading();

  useEffect(
    () => setSelectedCategory('*'),
    [filtersEnabled, categories.length]
  );

  const mobile = windowWidth && windowWidth <= MOBILE_MAX_WIDTH;
  const tablet = windowWidth && !mobile && windowWidth <= TABLET_MAX_WIDTH;

  const filteredImages = useMemo(() => {
    if (
      !images ||
      !categories.length ||
      selectedCategory === '*' ||
      !filtersEnabled
    ) {
      return images;
    }

    return images.filter(({ categories: imageCategories }) =>
      imageCategories.includes(selectedCategory)
    );
  }, [images, categories.length, selectedCategory, filtersEnabled]);

  return (
    <>
      {!!categories.length && filtersEnabled && (
        <CategoryList
          categories={categories}
          selectedCategory={selectedCategory}
          onCategorySelect={setSelectedCategory}
        />
      )}

      <LoadMore tablet={tablet} mobile={mobile} images={filteredImages}>
        {({ loadMore, end, showButton, buttonText, images }) =>
          images?.length ? (
            <div>
              <Layout mobile={mobile} tablet={tablet} images={images} />

              {!(end && allImagesLoaded) && showButton && (
                <LoadMoreButtonContainer
                  handleClick={loadMore}
                  text={buttonText}
                  disabled={end}
                />
              )}
            </div>
          ) : (
            filtersEnabled && <EmptyCategory />
          )
        }
      </LoadMore>

      <Lightbox sources={sources} allCategories={categories} />
    </>
  );
};

const LoadMoreButton = styled.div.attrs({
  className: `${meta.stylePrefix}-photo-gallery-loadMore`
})`
  width: fit-content;
  margin: 0 auto;
  margin-top: 40px;
  padding: 16px 60px;
  font-size: 15px;
  text-transform: uppercase;
  cursor: pointer;
  position: relative;
  top: 0;
  transition: 0.3s ease all;
  border-width: 2px;
  border-style: solid;

  border-color: ${({ $loadMoreButtonColor }) => $loadMoreButtonColor || '#111'};
  color: ${({ $loadMoreButtonColor }) => $loadMoreButtonColor || '#111'};

  &:hover {
    top: -4px;
  }
`;
